import { useCallback, useState } from 'react';
import { ActivityIndicator, Pressable, TextInput } from 'react-native';
import styled from 'styled-components/native';
import { REDESIGN_COLORS } from '../../styles/colors';
import {
  GUIDED_OUTCOME_TYPES,
  CHANNEL_OPTIONS,
} from '../../config/negotiationConfig';
import supabase from '../../utils/supabaseClient';
import { apiService } from '../../services/apiService';
import logger from '../../utils/logger';
import { useTier } from '../../context/TierContext';
import { handleProAccessError } from '../../utils/handleProAccessError';

// --- Styled Components ---

const Container = styled.View`
  padding: 16px;
  background-color: ${REDESIGN_COLORS.cardBg};
  border-radius: 12px;
  border-width: 1px;
  border-color: ${REDESIGN_COLORS.borderSubtle};
`;

const StepLabel = styled.Text`
  font-family: 'Satoshi-Bold';
  font-size: 13px;
  color: ${REDESIGN_COLORS.textSecondary};
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const StepTitle = styled.Text`
  font-family: 'Satoshi-Bold';
  font-size: 16px;
  color: ${REDESIGN_COLORS.textPrimary};
  margin-bottom: 12px;
`;

const OptionButton = styled(Pressable)`
  padding: 12px 16px;
  border-radius: 8px;
  border-width: 1px;
  border-color: ${({ $active }) =>
    $active ? REDESIGN_COLORS.ctaBlue : REDESIGN_COLORS.borderSubtle};
  background-color: ${({ $active }) =>
    $active ? 'rgba(5, 86, 247, 0.15)' : 'transparent'};
  margin-bottom: 8px;
`;

const OptionText = styled.Text`
  font-family: 'Satoshi-Medium';
  font-size: 14px;
  color: ${REDESIGN_COLORS.textPrimary};
`;

const PriceInputWrapper = styled.View`
  flex-direction: row;
  align-items: center;
  border-width: 1px;
  border-color: ${REDESIGN_COLORS.borderSubtle};
  border-radius: 8px;
  background-color: rgba(255, 255, 255, 0.05);
  margin-bottom: 12px;
`;

const CurrencyPrefix = styled.Text`
  font-family: 'Satoshi-Medium';
  font-size: 16px;
  color: ${REDESIGN_COLORS.textSecondary};
  padding-left: 14px;
`;

const SummaryInput = styled.View`
  border-width: 1px;
  border-color: ${REDESIGN_COLORS.borderSubtle};
  border-radius: 8px;
  background-color: rgba(255, 255, 255, 0.05);
  margin-bottom: 12px;
`;

const ActionRow = styled.View`
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  margin-top: 8px;
`;

const SubmitButton = styled(Pressable)`
  background-color: ${REDESIGN_COLORS.ctaBlue};
  padding: 12px 24px;
  border-radius: 8px;
  align-items: center;
  justify-content: center;
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
`;

const SubmitButtonText = styled.Text`
  font-family: 'Satoshi-Bold';
  font-size: 14px;
  color: ${REDESIGN_COLORS.textPrimary};
`;

const BackButton = styled(Pressable)`
  padding: 12px 16px;
`;

const BackButtonText = styled.Text`
  font-family: 'Satoshi-Medium';
  font-size: 14px;
  color: ${REDESIGN_COLORS.textSecondary};
`;

const ErrorText = styled.Text`
  font-family: 'Satoshi-Regular';
  font-size: 13px;
  color: #ef4444;
  margin-top: 8px;
`;

const RetryButton = styled(Pressable)`
  margin-top: 12px;
  padding: 10px 20px;
  border-radius: 8px;
  border-width: 1px;
  border-color: ${REDESIGN_COLORS.ctaBlue};
  align-self: flex-start;
`;

const RetryButtonText = styled.Text`
  font-family: 'Satoshi-Medium';
  font-size: 13px;
  color: ${REDESIGN_COLORS.ctaBlue};
`;

/**
 * GuidedInput - 3-step structured input form for guided mode.
 *
 * Step 1: Pick outcome type (from GUIDED_OUTCOME_TYPES)
 * Step 2: Counter-offer price input (shown only when outcome is 'counter_offer')
 * Step 3: Channel used + free-text summary
 *
 * On submit: INSERT dealer_reported message via Supabase, then trigger
 * draft generation via the edge function.
 *
 * @param {Object} props
 * @param {string} props.negotiationId - The negotiation UUID
 * @param {Function} props.onSubmit - Callback after successful submission
 */
const GuidedInput = ({ negotiationId, onSubmit }) => {
  const [step, setStep] = useState(1);
  const [outcomeType, setOutcomeType] = useState(null);
  const [counterOfferPrice, setCounterOfferPrice] = useState('');
  const [channel, setChannel] = useState(null);
  const [summary, setSummary] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [draftError, setDraftError] = useState(false);
  const { triggerUpgradeModal } = useTier();

  const handleOutcomeSelect = useCallback((value) => {
    setOutcomeType(value);
  }, []);

  const handleChannelSelect = useCallback((value) => {
    setChannel(value);
  }, []);

  const handleNext = useCallback(() => {
    if (step === 1 && outcomeType) {
      // Skip step 2 if outcome is not counter_offer
      if (outcomeType === 'counter_offer') {
        setStep(2);
      } else {
        setStep(3);
      }
    } else if (step === 2) {
      setStep(3);
    }
  }, [step, outcomeType]);

  const handleBack = useCallback(() => {
    if (step === 3) {
      setStep(outcomeType === 'counter_offer' ? 2 : 1);
    } else if (step === 2) {
      setStep(1);
    }
  }, [step, outcomeType]);

  const handleRetryDraft = useCallback(async () => {
    setDraftError(false);
    try {
      await apiService.requestNegotiationAction('generate_draft', {
        negotiation_id: negotiationId,
      });
    } catch (err) {
      if (handleProAccessError(err, triggerUpgradeModal, 'negotiation_generate_draft_retry')) return;
      logger.error('[GuidedInput] Retry draft generation failed:', err);
      setDraftError(true);
    }
  }, [negotiationId, triggerUpgradeModal]);

  const handleSubmit = useCallback(async () => {
    if (!outcomeType || !channel) return;

    setSubmitting(true);
    setError(null);
    setDraftError(false);

    try {
      // Build message content
      const content = {
        outcome_type: outcomeType,
        channel,
        summary: summary.trim() || undefined,
        ...(outcomeType === 'counter_offer' && counterOfferPrice.trim()
          ? { counter_offer_price: Number(counterOfferPrice.replace(/[^0-9.]/g, '')) }
          : {}),
      };

      // Step 1: INSERT dealer_reported message via Supabase
      const { data: { user } } = await supabase.auth.getUser();
      const { data: messageData, error: insertError } = await supabase
        .from('negotiation_messages')
        .insert({
          negotiation_id: negotiationId,
          user_id: user.id,
          role: 'dealer_reported',
          content: JSON.stringify(content),
          status: 'received',
          channel,
          structured_input: content,
        })
        .select('id')
        .single();

      if (insertError) throw insertError;

      // Update last_user_action_at
      await supabase
        .from('negotiations')
        .update({ last_user_action_at: new Date().toISOString() })
        .eq('id', negotiationId);

      // Step 2: Trigger draft generation (non-fatal if this fails)
      // Pass triggered_by_message_id so the worker's guided-mode handler can
      // read structured_input.outcome_type, set classification (hardball /
      // incremental_move / etc.), and bump dealer_total_rounds. Without it
      // the strategy selector sees a null classification + zero rounds and
      // mis-routes to an opening archetype on every dealer reply.
      try {
        await apiService.requestNegotiationAction('generate_draft', {
          negotiation_id: negotiationId,
          triggered_by_message_id: messageData?.id,
        });
      } catch (draftErr) {
        if (handleProAccessError(draftErr, triggerUpgradeModal, 'negotiation_generate_draft')) {
          return;
        }
        logger.warn('[GuidedInput] Draft generation trigger failed (message saved):', draftErr);
        setDraftError(true);
      }

      if (onSubmit) {
        onSubmit(messageData);
      }
    } catch (err) {
      logger.error('[GuidedInput] Submit failed:', err);
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }, [negotiationId, outcomeType, channel, summary, counterOfferPrice, onSubmit, triggerUpgradeModal]);

  // --- Step 1: Outcome type ---
  if (step === 1) {
    return (
      <Container>
        <StepLabel>Step 1 of 3</StepLabel>
        <StepTitle>What happened?</StepTitle>
        {GUIDED_OUTCOME_TYPES.map((option) => (
          <OptionButton
            key={option.value}
            $active={outcomeType === option.value}
            onPress={() => handleOutcomeSelect(option.value)}
          >
            <OptionText>{option.label}</OptionText>
          </OptionButton>
        ))}
        <ActionRow>
          <BackButton disabled />
          <SubmitButton
            onPress={handleNext}
            disabled={!outcomeType}
          >
            <SubmitButtonText>Next</SubmitButtonText>
          </SubmitButton>
        </ActionRow>
      </Container>
    );
  }

  // --- Step 2: Counter-offer price ---
  if (step === 2) {
    return (
      <Container>
        <StepLabel>Step 2 of 3</StepLabel>
        <StepTitle>What price did they offer?</StepTitle>
        <PriceInputWrapper>
          <CurrencyPrefix>{'\u00A3'}</CurrencyPrefix>
          <TextInput
            style={{
              flex: 1,
              fontFamily: 'Satoshi-Medium',
              fontSize: 16,
              color: REDESIGN_COLORS.textPrimary,
              paddingVertical: 12,
              paddingHorizontal: 12,
              outlineStyle: 'none',
            }}
            value={counterOfferPrice}
            onChangeText={setCounterOfferPrice}
            placeholder="e.g. 12,500"
            placeholderTextColor={REDESIGN_COLORS.textSecondary}
            keyboardType="numeric"
          />
        </PriceInputWrapper>
        <ActionRow>
          <BackButton onPress={handleBack}>
            <BackButtonText>Back</BackButtonText>
          </BackButton>
          <SubmitButton onPress={handleNext}>
            <SubmitButtonText>Next</SubmitButtonText>
          </SubmitButton>
        </ActionRow>
      </Container>
    );
  }

  // --- Step 3: Channel + summary ---
  return (
    <Container>
      <StepLabel>Step 3 of 3</StepLabel>
      <StepTitle>How did you communicate?</StepTitle>
      {CHANNEL_OPTIONS.map((option) => (
        <OptionButton
          key={option.value}
          $active={channel === option.value}
          onPress={() => handleChannelSelect(option.value)}
        >
          <OptionText>{option.label}</OptionText>
        </OptionButton>
      ))}

      <StepTitle style={{ marginTop: 16 }}>Anything else to add? (optional)</StepTitle>
      <SummaryInput>
        <TextInput
          style={{
            fontFamily: 'Satoshi-Regular',
            fontSize: 14,
            color: REDESIGN_COLORS.textPrimary,
            padding: 12,
            minHeight: 80,
            textAlignVertical: 'top',
            outlineStyle: 'none',
          }}
          value={summary}
          onChangeText={setSummary}
          placeholder="e.g. They seemed keen, mentioned another buyer..."
          placeholderTextColor={REDESIGN_COLORS.textSecondary}
          multiline
          numberOfLines={3}
        />
      </SummaryInput>

      {error && <ErrorText>{error}</ErrorText>}

      {draftError && (
        <>
          <ErrorText>
            Your update was saved, but coaching generation failed.
          </ErrorText>
          <RetryButton onPress={handleRetryDraft}>
            <RetryButtonText>Retry coaching generation</RetryButtonText>
          </RetryButton>
        </>
      )}

      <ActionRow>
        <BackButton onPress={handleBack}>
          <BackButtonText>Back</BackButtonText>
        </BackButton>
        <SubmitButton
          onPress={handleSubmit}
          disabled={!channel || submitting}
        >
          {submitting ? (
            <ActivityIndicator size="small" color={REDESIGN_COLORS.textPrimary} />
          ) : (
            <SubmitButtonText>Submit</SubmitButtonText>
          )}
        </SubmitButton>
      </ActionRow>
    </Container>
  );
};

export default GuidedInput;
